import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMyTeamTree, recruitTeamMember } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, ShieldCheck, UserPlus, Users, ChevronRight, ChevronDown, ListTree, LayoutGrid } from 'lucide-react';
import toast from 'react-hot-toast';
import { roleLabel, ASSIGNABLE_ROLES } from '../utils/roles';

function Header({ admin, onLogout }) {
  return (
    <header style={{
      background: 'linear-gradient(135deg, #1D9E75 0%, #0F6E56 100%)',
      color: '#fff', padding: '0 28px', height: 68,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      position: 'sticky', top: 0, zIndex: 200,
      boxShadow: '0 2px 16px rgba(0,0,0,0.18)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 42, height: 42, background: 'rgba(255,255,255,0.18)',
          borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13, border: '1.5px solid rgba(255,255,255,0.3)'
        }}>SRG</div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>SRG Real Estate Pvt. Ltd.</div>
          <div style={{ fontSize: 11.5, opacity: 0.82, marginTop: 1 }}>My Team — Reporting Hierarchy</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Link to="/associate" style={{
          fontSize: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 14px',
          borderRadius: 20, color: '#fff', textDecoration: 'none', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6, border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <LayoutGrid size={13} /> Inventory
        </Link>
        <div style={{
          fontSize: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 14px',
          borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6,
          border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <ShieldCheck size={13} /> {admin?.name}
        </div>
        <button onClick={onLogout} style={{
          fontSize: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 14px',
          borderRadius: 20, color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit', fontWeight: 600
        }}>
          <LogOut size={13} /> Logout
        </button>
      </div>
    </header>
  );
}

const ROLE_COLORS = {
  clm: { bg: '#EDE7F6', color: '#5E35B1' },
  bdo: { bg: 'var(--blue-light)', color: '#185FA5' },
  sales_rep: { bg: '#FFF3E0', color: '#B36A00' },
  crm: { bg: '#FCE4EC', color: '#AD1457' },
  associate: { bg: 'var(--green-light)', color: 'var(--green-dark)' },
  leader: { bg: 'var(--blue-light)', color: '#185FA5' }, // legacy
};

function TreeNode({ node, isRoot, depth }) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const roleStyle = ROLE_COLORS[node.role] || ROLE_COLORS.associate;

  return (
    <div style={{ marginLeft: depth === 0 ? 0 : 22, position: 'relative' }}>
      {depth > 0 && (
        <div style={{
          position: 'absolute', left: -14, top: 0, bottom: 0, width: 1,
          borderLeft: '1.5px dashed var(--border)'
        }} />
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        borderRadius: 10, border: '1.5px solid var(--border)', background: isRoot ? 'var(--green-light)' : '#fff',
        marginBottom: 6, position: 'relative'
      }}>
        {hasChildren ? (
          <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 0 }}>
            {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        ) : (
          <span style={{ width: 15 }} />
        )}
        <div style={{
          width: 30, height: 30, borderRadius: '50%', background: roleStyle.bg, color: roleStyle.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0
        }}>
          {node.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            {node.name}
            {isRoot && <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>(You)</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{node.email}</div>
        </div>
        <span style={{
          fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
          background: roleStyle.bg, color: roleStyle.color,
        }}>
          {roleLabel(node.role)}
        </span>
        {!node.is_active && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--red-light)', color: 'var(--red)' }}>
            Inactive
          </span>
        )}
        {hasChildren && (
          <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Users size={12} /> {node.children.length}
          </span>
        )}
      </div>
      {hasChildren && open && (
        <div>
          {node.children.map(child => (
            <TreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function RecruitModal({ onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('associate');

  const mutation = useMutation({
    mutationFn: recruitTeamMember,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-team-tree'] });
      toast.success(`${name} added to your team!`);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !email || !password) return toast.error('All fields required');
    if (password.length < 8) return toast.error('Password must be at least 8 characters');
    mutation.mutate({ name, email, password, role });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head"><h3>Add Team Member</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Vikas Kumar" required />
            </div>
            <div className="form-group">
              <label className="form-label">Role *</label>
              <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
                {ASSIGNABLE_ROLES.map(r => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="vikas@example.com" required />
            </div>
            <div className="form-group">
              <label className="form-label">Password * (min 8 chars)</label>
              <input type="password" className="form-input" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--text3)' }}>
              They'll be added under you in the team tree, and will automatically get access to the same projects you do.
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green btn-sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Adding...' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TeamTree() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [showRecruit, setShowRecruit] = useState(false);

  const { data: tree, isLoading } = useQuery({ queryKey: ['my-team-tree'], queryFn: getMyTeamTree });

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  return (
    <>
      <Header admin={admin} onLogout={handleLogout} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 20px 40px' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ListTree size={20} color="var(--green)" /> My Team
            </h2>
            <p style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 2 }}>
              Everyone you've added, and everyone they've added — full downstream chain.
            </p>
          </div>
          <button className="btn btn-green" onClick={() => setShowRecruit(true)}>
            <UserPlus size={15} /> Add Team Member
          </button>
        </div>

        <div className="stat-card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '20px 16px' }}>
          <div>
            <div className="stat-val" style={{ color: 'var(--green)' }}>{tree?.total_team_size ?? 0}</div>
            <div className="stat-lbl">Total Team Members</div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
            ) : !tree ? (
              <div className="empty-state"><p>Unable to load your team.</p></div>
            ) : (
              <TreeNode node={tree} isRoot depth={0} />
            )}
            {tree && (!tree.children || tree.children.length === 0) && (
              <div className="empty-state" style={{ paddingTop: 20 }}>
                <Users size={32} />
                <p>You haven't added anyone to your team yet.</p>
                <button className="btn btn-green btn-sm" onClick={() => setShowRecruit(true)}>
                  <UserPlus size={14} /> Add Your First Team Member
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showRecruit && <RecruitModal onClose={() => setShowRecruit(false)} />}
    </>
  );
}
